import {
  Autocomplete,
  Box,
  createFilterOptions,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { PersonAvatar } from '../people';

import { FIELD_HEIGHT_SX } from './FormField';

/**
 * Pick one person by typing (brief §3, Who's involved): the MUI `Autocomplete`
 * that v1's `EmployeePicker` and the New project dialog use, with each option
 * as the reference draws it — initials, the name, and a second line (the
 * department, or a contact's title) — and the chosen person's initials in the
 * field. Searches name and second line alike.
 *
 * One picker for employees and the customer's contacts; v1's employee-only
 * `EmployeePicker` has no second line and goes with the v1 code (slice 7).
 */
export type PersonOption = { id: string; name: string; sub: string };

export type PersonPickerProps = {
  id: string;
  options: PersonOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  /** Owners are always set; the contact may be cleared. */
  clearable?: boolean;
};

const filter = createFilterOptions<PersonOption>({
  stringify: (option) => `${option.name} ${option.sub}`,
  limit: 40,
});

export const PersonPicker = ({
  id,
  options,
  value,
  onChange,
  clearable = false,
}: PersonPickerProps) => {
  const { t } = useTranslation('pmDetails');
  const selected = options.find((option) => option.id === value) ?? null;
  return (
    <Autocomplete
      id={id}
      options={options}
      value={selected}
      filterOptions={filter}
      disableClearable={!clearable && selected !== null}
      clearText={t('people.Clear')}
      onChange={(_event, next) => {
        if (next || clearable) onChange(next?.id ?? null);
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
      noOptionsText={t('people.NoOneMatches')}
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={key} {...props}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <PersonAvatar name={option.name} />
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="bodyRegular" component="span">
                {option.name}
              </Typography>
              <Typography variant="label" component="span" sx={{ color: 'var(--color-fog-80)' }}>
                {option.sub}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={t('people.SelectPerson')}
          sx={FIELD_HEIGHT_SX}
          slotProps={{
            ...params,
            input: {
              ...params.InputProps,
              startAdornment: selected ? (
                <Box sx={{ display: 'inline-flex', ml: 0.5 }}>
                  <PersonAvatar name={selected.name} />
                </Box>
              ) : null,
            },
            htmlInput: { ...params.inputProps, 'data-automation-id': id },
          }}
        />
      )}
    />
  );
};
